// Recruiting pipeline — single source of truth for candidate status ids.
//
// Ids are persisted in candidate_state, as plaintext, so they must stay stable.
//
// Status is per ASSIGNMENT, not per person — see the section at the bottom for
// the shape and the read helpers. Nothing should index status by a bare
// personId.

export const STATUSES = [
  { id: "target", label: "Target" },
  { id: "outreach", label: "Outreach" },
  { id: "longlist", label: "Longlist" },
  { id: "shortlist", label: "Shortlist" },
  { id: "first_interview", label: "1st interview" },
  { id: "interviewing", label: "Interviewing" },
  { id: "offer", label: "Offer" },
  { id: "placement", label: "Placement" },
  { id: "rejected", label: "Rejected" },
];

// A candidate that has never been actioned.
export const DEFAULT_STATUS = "target";

// Semantic groups for summary views. Mirrors the colour grouping documented on
// STATUS_COLORS in Outreach.js: outreach = awaiting, longlist→interviewing =
// active, offer/placement = won, rejected = lost.
export const ACTIVE_STATUSES = [
  "longlist",
  "shortlist",
  "first_interview",
  "interviewing",
];
export const WON_STATUSES = ["offer", "placement"];

// Contacted and waiting on them — successor to the legacy `contacted` id.
export const AWAITING_STATUS = "outreach";

// Stages where a meeting is booked or has already happened. The legacy
// `meeting` id migrated onto `interviewing`; `first_interview` is equally a
// meeting, so counting only the former would undercount.
export const MEETING_STATUSES = ["first_interview", "interviewing"];

// Position in the funnel; -1 for unknown ids. Used to sort by progression.
export const statusRank = (id) => STATUSES.findIndex((s) => s.id === id);

export const statusLabel = (id) =>
  (STATUSES.find((s) => s.id === id) || STATUSES[0]).label;

/* ──────────────────────────────────────────────────────────────────────────
 * Job-scoped status
 *
 * Status belongs to a candidate ON A JOB, not to the candidate — the same
 * person can be shortlisted for one role and still a target on another. The
 * server stores one row per candidate and the client holds them as:
 *
 *   states = { [candidateId]: { jobs: { [jobId]: "shortlist" } } }
 *
 * There is no candidate-level status: a candidate on no job is a Target until
 * you assign them to something. The server only returns stages for jobs the
 * candidate is still linked to, so a stage can never outlive its assignment.
 * ────────────────────────────────────────────────────────────────────────── */

/** Status for one person on one job. Unset assignments are Target. */
export function statusFor(states, jobId, personId) {
  const st = states?.[personId];
  if (!st || !jobId) return DEFAULT_STATUS;
  return st.jobs?.[jobId] || DEFAULT_STATUS;
}


/** Furthest-along of a bare list of statuses. See furthestStatus for the rule. */
function furthestOf(values) {
  if (!values || values.length === 0) return DEFAULT_STATUS;
  const live = values.filter((s) => s !== "rejected");
  const pool = live.length > 0 ? live : values;
  return pool.reduce((a, b) => (statusRank(b) > statusRank(a) ? b : a));
}

/**
 * The one status to show when no job is in scope: how far this person has got
 * anywhere.
 *
 * Rejected is deliberately not "furthest" despite ranking last — being turned
 * down for one role shouldn't present as the headline when another is live. It
 * only surfaces when every assignment is rejected.
 *
 * Returns { status, varies } — `varies` drives the "differs per job" marker.
 */
export function furthestStatus(states, personId) {
  const values = Object.values(states?.[personId]?.jobs || {});
  return { status: furthestOf(values), varies: new Set(values).size > 1 };
}
